import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, Modal, PlayerAvatar, Toast } from '../../components/common';
import { SVG_AVATARS } from '../../components/common/PlayerAvatar';
import AdminNav from '../../components/admin/AdminNav';

const emptyForm = { name: '', email: '', country: '', avatar: 'avatar1', phone: '', city: '' };

export default function AdminPlayers() {
  const { players, addPlayer, updatePlayer, fetchPlayers } = useApp();
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showAvatars, setShowAvatars] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    if (players.length === 0) fetchPlayers();
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const openNew = () => { setForm(emptyForm); setEditId(null); setModal(true); };
  const openEdit = (p) => { setForm(p); setEditId(p.id); setModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await updatePlayer(editId, form);
        setToast({ show: true, message: 'Player updated successfully', type: 'success' });
      } else {
        await addPlayer(form);
        setToast({ show: true, message: 'Player added successfully', type: 'success' });
      }
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
      setModal(false);
    } catch (err) {
      setToast({ show: true, message: err.message || 'Failed to save player', type: 'error' });
      setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const filtered = players.filter(p => p.name.toLowerCase().includes(search.toLowerCase())).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div style={styles.container}>
      <Toast message={toast.message} type={toast.type} isVisible={toast.show} />
      <AdminNav />
      <div style={styles.header}>
        <h1 style={styles.title}>Players</h1>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <input style={styles.search} placeholder="Search players..." value={search} onChange={e => setSearch(e.target.value)} />
        <button onClick={openNew} style={styles.addBtn}>+ Add</button>
      </div>

      {isMobile ? (
        <div style={styles.mobileCards}>
          {filtered.map(p => (
            <div key={p.id} style={styles.mobileCard}>
              <div style={styles.mobileCardHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <PlayerAvatar name={p.name} photo={p.avatar} size={40} />
                  <div>
                    <div style={styles.mobileCardName}>{p.name}</div>
                    <div style={styles.mobileCardCountry}>{p.country}{p.city ? `, ${p.city}` : ''}</div>
                    {p.phone && <div style={{ fontSize: 11, color: '#8892A6', marginTop: 2 }}>{p.phone}</div>}
                  </div>
                </div>
              </div>
              <div style={styles.mobileStats}>
                <div style={styles.mobileStat}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#43A047' }}>{p.wins}</span>
                  <span style={{ fontSize: 10, color: '#8892A6' }}>Won</span>
                </div>
                <div style={styles.mobileStat}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#E53935' }}>{p.losses}</span>
                  <span style={{ fontSize: 10, color: '#8892A6' }}>Lost</span>
                </div>
                <div style={styles.mobileStat}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#00205B' }}>{p.matchesPlayed}</span>
                  <span style={{ fontSize: 10, color: '#8892A6' }}>Played</span>
                </div>
                <div style={styles.mobileStat}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: '#F9A825' }}>{p.titles || 0}</span>
                  <span style={{ fontSize: 10, color: '#8892A6' }}>Titles</span>
                </div>
              </div>
              <button onClick={() => openEdit(p)} style={styles.mobileEditBtn}>Edit Player</button>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.table}>
          <div style={styles.tableHeader}>
            <span style={{ flex: 2 }}>Player</span>
            <span style={{ flex: 1 }}>Country</span>
            <span style={{ flex: 1 }}>City</span>
            <span style={{ flex: 0.5 }}>W</span>
            <span style={{ flex: 0.5 }}>L</span>
            <span style={{ flex: 0.5 }}>Played</span>
            <span style={{ flex: 0.5 }}>Titles</span>
            <span style={{ flex: 1 }}>Actions</span>
          </div>
          {filtered.map(p => (
            <div key={p.id} style={styles.tableRow}>
              <span style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                <PlayerAvatar name={p.name} photo={p.avatar} size={32} /> {p.name}
              </span>
              <span style={{ flex: 1 }}>{p.country}</span>
              <span style={{ flex: 1 }}>{p.city || '-'}</span>
              <span style={{ flex: 0.5, color: '#43A047', fontWeight: 600 }}>{p.wins}</span>
              <span style={{ flex: 0.5, color: '#E53935', fontWeight: 600 }}>{p.losses}</span>
              <span style={{ flex: 0.5 }}>{p.matchesPlayed}</span>
              <span style={{ flex: 0.5 }}>{p.titles || 0}</span>
              <span style={{ flex: 1 }}>
                <button onClick={() => openEdit(p)} style={styles.editBtn}>Edit</button>
              </span>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modal} onClose={() => setModal(false)} title={editId ? 'Edit Player' : 'Add Player'}>
        <form onSubmit={handleSave}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <PlayerAvatar name={form.name || 'Player'} photo={form.avatar} size={64} />
          </div>
          <label style={styles.label}>Profile Avatar</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <button type="button" onClick={() => setShowAvatars(!showAvatars)} style={{ flex: 1, padding: '10px 14px', background: showAvatars ? '#00205B' : '#F8F9FC', color: showAvatars ? '#BDFF00' : '#5F6B80', border: '1.5px solid #EEF1F6', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Pick Avatar</button>
          </div>
          {showAvatars && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12, padding: 12, background: '#F8F9FC', borderRadius: 10, border: '1px solid #EEF1F6' }}>
              {SVG_AVATARS.map(av => (
                <button type="button" key={av.id} onClick={() => { set('avatar', av.id); setShowAvatars(false); }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: 8, borderRadius: 10, border: form.avatar === av.id ? '2px solid #00205B' : '1px solid #EEF1F6', background: form.avatar === av.id ? '#E3F2FD' : '#fff', cursor: 'pointer' }}>
                  <PlayerAvatar photo={av.id} name="" size={36} />
                  <span style={{ fontSize: 9, color: '#5F6B80', fontWeight: 600 }}>{av.label}</span>
                </button>
              ))}
            </div>
          )}
          <label style={styles.label}>Name <span style={{ color: '#E53935' }}>*</span></label>
          <input style={styles.input} value={form.name} onChange={e => set('name', e.target.value)} required />
          <label style={styles.label}>Email</label>
          <input style={styles.input} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="player@email.com" />
          <label style={styles.label}>Phone <span style={{ color: '#E53935' }}>*</span></label>
          <input style={styles.input} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="e.g. 9876543210" required />
          <label style={styles.label}>City</label>
          <input style={styles.input} value={form.city} onChange={e => set('city', e.target.value)} placeholder="e.g. Pune" />
          <label style={styles.label}>Country <span style={{ color: '#E53935' }}>*</span></label>
          <input style={styles.input} value={form.country} onChange={e => set('country', e.target.value)} required />
          <button type="submit" disabled={saving} style={{ ...styles.addBtn, width: '100%', marginTop: 16, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>{saving ? 'Saving...' : editId ? 'Update' : 'Add'} Player</button>
        </form>
      </Modal>
    </div>
  );
}

const styles = {
  container: { minHeight: 'calc(100vh - 52px)', background: 'var(--bg)', padding: '16px' },
  header: { marginBottom: 16 },
  title: { fontSize: 24, fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif", marginBottom: 4 },
  search: { padding: '8px 12px', border: '1px solid #EEF1F6', borderRadius: 8, fontSize: 13, outline: 'none', flex: 1, minWidth: 140, background: '#fff' },
  addBtn: { padding: '8px 16px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' },
  table: { background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,32,91,0.06)', border: '1px solid #EEF1F6' },
  tableHeader: { display: 'flex', padding: '12px 20px', background: '#F8F9FC', color: '#5F6B80', fontWeight: 700, fontSize: 12 },
  tableRow: { display: 'flex', padding: '14px 20px', borderBottom: '1px solid #EEF1F6', alignItems: 'center', fontSize: 13 },
  editBtn: { padding: '6px 14px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 600 },
  label: { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block', marginTop: 12 },
  input: { width: '100%', padding: 10, border: '2px solid #e0e0e0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' },
  mobileCards: { display: 'flex', flexDirection: 'column', gap: 12 },
  mobileCard: { background: '#fff', padding: 14, borderRadius: 12, border: '1px solid #EEF1F6', boxShadow: '0 2px 8px rgba(0,32,91,0.04)' },
  mobileCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #EEF1F6' },
  mobileCardName: { fontSize: 15, fontWeight: 700, color: '#00205B', fontFamily: "'Outfit', sans-serif" },
  mobileCardCountry: { fontSize: 12, color: '#8892A6', marginTop: 2 },
  mobileRank: { fontSize: 18, fontWeight: 800, color: '#00205B', fontFamily: "'Outfit', sans-serif" },
  mobileStats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 },
  mobileStat: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 4px', background: '#F8F9FC', borderRadius: 8 },
  mobileEditBtn: { width: '100%', padding: '10px', background: '#00205B', color: '#BDFF00', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 },
};
